/**
 * 메뉴별 상태 코드 → 한글 라벨 매핑
 *
 * 기존 각 서비스/페이지에 분산되어 있던 라벨을 일괄 모음:
 *   - sales     : services/sales.ts SALES_STATUS_LABELS
 *   - purchase  : pages/purchase/PurchaseListPage.tsx PURCHASE_STATUS_LABELS
 *   - production: services/production.ts (ProductionStatus enum 주석)
 *   - claims    : services/claims.ts (이미 한글값)
 *   - home_styling: services/homeStyling.ts statusLabels
 *
 * 사용: `getStatusLabel(menu, statusCode)` → 매칭되는 라벨이 있으면 한글, 없으면 원본 코드 반환.
 */

export const STATUS_LABELS: Record<string, Record<string, string>> = {
  sales: {
    quote_draft: '견적서 초안',
    order_proposal: '주문서 제안',
    sales_complete: '판매완료',
    quote_request: '견적 요청',
    order_received: '주문 접수',
    ready_to_ship: '출고 준비',
    partial_shipped: '부분 출고',
    shipped: '출고 완료',
    delivered: '배송 완료',
    partial_cancel_request: '부분 취소 요청',
    cancel_request: '취소 요청',
    cancelled: '취소',
    partial_return_request: '부분 반품 요청',
    return_request: '반품 요청',
    returned: '반품',
    partial_exchange_request: '부분 교환 요청',
    exchange_request: '교환 요청',
    exchanged: '교환',
    quote: '견적',
    order: '주문',
    preparing: '준비중',
    shipping: '배송중',
    completed: '완료',
  },
  purchase: {
    plan: '발주계획',
    request: '발주요청',
    complete: '발주완료',
    received: '입고완료',
    pending: '대기',
    approved: '승인됨',
    rejected: '반려',
    cancelled: '취소',
  },
  production: {
    draft: '초안',
    happy_call_completed: '해피콜 완료',
    instruction_pending: '지시대기',
    work_order_completed: '작업지시완료',
    partially_completed: '부분 완료',
    after_service: 'A/S',
    on_hold: '보류',
    completed: '완료',
    cancelled: '취소',
    pending: '대기',
  },
  claims: {
    // 이미 한글값이지만 영문 fallback 키도 포함
    open: '접수',
    in_progress: '처리 중',
    resolved: '해결됨',
    closed: '종료',
    // 한글 key는 그대로 통과 (항등 매핑)
    '접수': '접수',
    '회수접수': '회수접수',
    '회수중': '회수중',
    '입고완료': '입고완료',
    'A/S 작업중': 'A/S 작업중',
    '출고완료': '출고완료',
    '최종완료': '최종완료',
  },
  home_styling: {
    consultation: '방문상담신청',
    production: '제작중',
    scheduled: '설치일정 확정',
    shipped: '발송완료',
    completed: '설치완료',
    hold: '보류',
    unsigned: '미계약',
    cancelled: '방문전 취소',
  },
};

/**
 * 공통 상태 라벨 (메뉴 미지정 시 fallback).
 */
const COMMON_FALLBACK: Record<string, string> = {
  draft: '초안',
  pending: '대기',
  in_progress: '진행 중',
  approved: '승인됨',
  rejected: '반려',
  completed: '완료',
  cancelled: '취소',
  open: '열림',
  closed: '종료',
  on_hold: '보류',
};

/**
 * 메뉴·상태 코드 → 한글 라벨. 매칭 없으면 원본 상태 그대로 반환.
 */
export function getStatusLabel(menu: string | undefined, status: string | undefined | null): string {
  if (!status) return '';
  const menuMap = menu ? STATUS_LABELS[menu] : undefined;
  if (menuMap && menuMap[status]) return menuMap[status];
  if (COMMON_FALLBACK[status]) return COMMON_FALLBACK[status];
  return status;
}
